import { Loyalty } from '@getjust/api-schema';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import { loyaltyDiscountCodeAtom, loyaltyRedeemedAtom } from '$business/atoms/loyalty';
import { AnalyticsEventName, useTrackAnalytics } from '$src/hooks/client/useTrackAnalytics';
import { useAddDiscount } from '$src/hooks/mutations/useAddDiscount';
import { useFormMessageAtom } from '$src/hooks/state';
import { gateway } from '$src/http';

const MUTATION_LOYALTY_REDEEM_KEY = 'MUTATION/LOYALTY_REDEEM';

interface RedeemInput {
  email: string;
  redemptionOptionId: number;
  currency: string;
}

export const useLoyaltyRedeem = () => {
  const { formMessage } = useFormMessageAtom();
  const queryClient = useQueryClient();
  const [redeemed, setRedeemed] = useAtom(loyaltyRedeemedAtom);
  const [, setLoyaltyDiscountCode] = useAtom(loyaltyDiscountCodeAtom);
  const { addDiscount } = useAddDiscount();
  const { track } = useTrackAnalytics();

  const shopId = formMessage?.shopId;

  const mutation = useMutation({
    mutationKey: [MUTATION_LOYALTY_REDEEM_KEY, shopId],
    mutationFn: async (input: RedeemInput) => {
      if (!shopId) {
        return { outcome: 'errored' as const, error: new Error('Shop ID is required') };
      }

      const response = await gateway.post<Loyalty.LoyaltyCreateRedemptionResponseBody>(
        `/api/${encodeURIComponent(shopId)}/loyalty/redeem`,
        input,
      );
      return response.data;
    },
    onSuccess: async (data) => {
      if (data.outcome === 'created' && data.discountCode) {
        track(AnalyticsEventName.LOYALTY_REDEEM_SUCCEEDED, { discountCode: data.discountCode });
        setLoyaltyDiscountCode(data.discountCode);
        setRedeemed(true);

        try {
          await addDiscount({ code: data.discountCode });
        } catch {
          track(AnalyticsEventName.LOYALTY_REDEEM_DISCOUNT_AUTO_APPLY_FAILED, {
            discountCode: data.discountCode,
          });
        }

        queryClient.invalidateQueries({ queryKey: ['QUERY/LOYALTY_BALANCE'] });
      }
    },
    onError: () => {
      track(AnalyticsEventName.LOYALTY_REDEEM_FAILED);
    },
  });

  return {
    redeem: mutation.mutateAsync,
    isLoading: mutation.isPending,
    isError: mutation.isError,
    isRedeemed: redeemed,
    result: mutation.data,
    error: mutation.error,
    reset: mutation.reset,
  };
};
